import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import './SearchableDropdown.css';

const OPTION_ROW_HEIGHT = 40;
const VIRTUALIZE_AFTER = 80;
const VIRTUAL_OVERSCAN = 5;
const DROPDOWN_VIEWPORT_HEIGHT = 280;
const normalizeSearchText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase();

/**
 * SearchableDropdown — premium searchable combobox.
 *
 * Round 83 — promoted from a Q&A-local helper to a shared
 * UI primitive. We need it in two places now:
 *   1. The Q&A forum filters (already used it) — the user
 *      asked for a search box on the trip wizard's País /
 *      Cidade selects too, and the wizard has 200+ countries
 *      with thousands of cities; a native <select> is
 *      unusable on mobile.
 *   2. The TripWizard destinations row (the País + Cidade
 *      fields at the very top of the create-trip flow).
 *
 * Behaviour:
 *   - Type to filter the option list (case-insensitive,
 *     substring match on `labelKey`)
 *   - Arrow keys to move highlight, Enter to commit, Escape
 *     to close, Backspace on empty input to clear the value
 *   - Click outside to close
 *   - The `options` array is `{ [valueKey]: any, [labelKey]: string }[]`
 *   - `value` is `null` or matches one of `options[i][valueKey]`
 *   - When `value` is set, the input shows the resolved label
 *     and is read-only (single-select)
 *
 * Styled in `SearchableDropdown.css` (extracted from the
 * original `qanda.css` `.gm-dd` block so the TripWizard can
 * reuse the same look).
 */
const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  labelKey = 'label',
  valueKey = 'value',
  emptyMessage = 'Nenhum resultado encontrado',
  className = '',
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  required = false,
  id,
  allowCustom = false,
  remoteOptions = [],
  remoteLoading = false,
  onSearch,
}) => {
  const [search, setSearch] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [listScrollTop, setListScrollTop] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const nativePickerTransitionRef = useRef(false);
  const [nativePickerMode, setNativePickerMode] = useState('idle');
  const androidBrowser = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');
  const [nativeAndroidReady, setNativeAndroidReady] = useState(() => (
    typeof document !== 'undefined'
      && document.documentElement.classList.contains('gm-native-android')
  ));
  const generatedId = useId();
  const optionsId = `${id || 'gm-dd'}-options-${generatedId.replace(/:/g, '')}`;
  const isTripDestinationPicker = Boolean(id && /^wiz-destination-(country|city)-\d+$/.test(id));
  const isNativeDestinationPicker = isTripDestinationPicker && nativeAndroidReady;

  useEffect(() => {
    if (!isTripDestinationPicker || nativeAndroidReady || typeof document === 'undefined') return undefined;
    const handleNativeReady = () => {
      if (document.documentElement.classList.contains('gm-native-android')) {
        setNativeAndroidReady(true);
      }
    };
    document.addEventListener('gm-native-android-ready', handleNativeReady);
    handleNativeReady();
    return () => document.removeEventListener('gm-native-android-ready', handleNativeReady);
  }, [isTripDestinationPicker, nativeAndroidReady]);

  const mergedOptions = useMemo(() => {
    const optionValues = new Set(options.map((opt) => String(opt[valueKey])));
    return [
      ...options,
      ...remoteOptions.filter((opt) => !optionValues.has(String(opt[valueKey]))),
    ];
  }, [options, remoteOptions, valueKey]);
  const normalizedSearch = normalizeSearchText(search);
  const filtered = useMemo(() => {
    if (!showOptions) return [];
    // Opening a dropdown with an empty query should render the original list
    // directly; filtering thousands of places here was causing Android-only
    // stalls before the first row appeared.
    if (!normalizedSearch) return mergedOptions;
    return mergedOptions.filter((opt) => {
      const searchableValues = [
        opt[labelKey],
        ...(Array.isArray(opt.searchTerms) ? opt.searchTerms : []),
      ];
      return searchableValues.some((candidate) => normalizeSearchText(candidate).includes(normalizedSearch));
    });
  }, [labelKey, mergedOptions, normalizedSearch, showOptions]);
  const selectedLabel = useMemo(() => (value
    ? mergedOptions.find((opt) => opt[valueKey] === value)?.[labelKey]
      || (allowCustom ? String(value) : '')
    : ''), [allowCustom, labelKey, mergedOptions, value, valueKey]);

  // Destination fields must also support places that are not present in the
  // reference catalogue (villages, parks, beaches, mountains, etc.). Keep
  // this opt-in so the shared dropdown remains a strict selector everywhere
  // else in the application.
  const customSearch = String(search || '').trim();
  const normalizedCustomSearch = normalizeSearchText(customSearch);
  const hasExactMatch = allowCustom && customSearch.length >= 2 && filtered.some((opt) => (
    [opt[labelKey], ...(Array.isArray(opt.searchTerms) ? opt.searchTerms : [])]
      .some((candidate) => normalizeSearchText(candidate).trim() === normalizedCustomSearch)
  ));
  const showCustomOption = allowCustom && customSearch.length >= 2 && !hasExactMatch;
  const virtualized = filtered.length > VIRTUALIZE_AFTER;
  const listStart = virtualized
    ? Math.max(0, Math.floor(listScrollTop / OPTION_ROW_HEIGHT) - VIRTUAL_OVERSCAN)
    : 0;
  const listEnd = virtualized
    ? Math.min(
        filtered.length,
        Math.ceil((listScrollTop + DROPDOWN_VIEWPORT_HEIGHT) / OPTION_ROW_HEIGHT) + VIRTUAL_OVERSCAN,
      )
    : filtered.length;
  const visibleOptions = virtualized ? filtered.slice(listStart, listEnd) : filtered;

  useEffect(() => () => {
    if (scrollFrameRef.current === null) return;
    if (window.cancelAnimationFrame) window.cancelAnimationFrame(scrollFrameRef.current);
    else window.clearTimeout(scrollFrameRef.current);
  }, []);

  useEffect(() => {
    if (!showOptions || !virtualized || focusedIndex < 0 || !listRef.current) return;
    const list = listRef.current;
    const top = focusedIndex * OPTION_ROW_HEIGHT;
    const bottom = top + OPTION_ROW_HEIGHT;
    const viewport = list.clientHeight || DROPDOWN_VIEWPORT_HEIGHT;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + viewport) list.scrollTop = bottom - viewport;
  }, [focusedIndex, showOptions, virtualized]);

  const handleListScroll = () => {
    if (scrollFrameRef.current !== null) return;
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 16));
    scrollFrameRef.current = schedule(() => {
      scrollFrameRef.current = null;
      setListScrollTop(listRef.current?.scrollTop || 0);
    });
  };

  const handleSearchChange = (event) => {
    const next = event.target.value;
    setSearch(next);
    setListScrollTop(0);
    setFocusedIndex(-1);
    if (listRef.current) listRef.current.scrollTop = 0;
    onSearch?.(next);
  };

  const handleSelect = (val) => {
    onChange(val);
    setShowOptions(false);
    setSearch('');
    onSearch?.('');
    setFocusedIndex(-1);
    setListScrollTop(0);
    if (isNativeDestinationPicker) {
      setNativePickerMode('idle');
      inputRef.current?.blur();
    }
  };

  const handleNativePickerPointerDown = (event) => {
    if (!isNativeDestinationPicker || disabled || event.pointerType === 'mouse') return;

    const input = inputRef.current;
    if (!input) return;

    if (nativePickerMode === 'search') return;

    // On Android, the first tap is for choosing from the list. Keep the input
    // read-only until the list is visible so WebView does not open the IME.
    event.preventDefault();
    if (nativePickerMode === 'idle') {
      setNativePickerMode('list');
      window.requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
      return;
    }

    // A second tap explicitly opts into typing/searching. Blur/refocus after
    // making it editable so Android opens the keyboard for that tap.
    nativePickerTransitionRef.current = true;
    setNativePickerMode('search');
    window.requestAnimationFrame(() => {
      const currentInput = inputRef.current;
      currentInput?.blur();
      window.requestAnimationFrame(() => {
        nativePickerTransitionRef.current = false;
        inputRef.current?.focus({ preventScroll: true });
      });
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && value && !search) {
      e.preventDefault();
      onChange(null);
      setSearch('');
      setListScrollTop(0);
      setShowOptions(true);
      return;
    }
    if (!showOptions && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setListScrollTop(0);
      setShowOptions(true);
      return;
    }
    if (showOptions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((p) => (p < filtered.length - 1 ? p + 1 : p));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((p) => (p > 0 ? p - 1 : -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (focusedIndex >= 0 && filtered[focusedIndex]) {
          handleSelect(filtered[focusedIndex][valueKey]);
        } else if (showCustomOption) {
          handleSelect(customSearch);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowOptions(false);
        setFocusedIndex(-1);
      }
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowOptions(false);
        if (isNativeDestinationPicker) setNativePickerMode('idle');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isNativeDestinationPicker]);

  return (
    <div
      ref={dropdownRef}
      className={`gm-dd ${disabled ? 'gm-dd--disabled' : ''} ${showOptions ? 'gm-dd--open' : ''} ${className}`}
    >
      <div className="gm-dd__input-wrap" onPointerDownCapture={handleNativePickerPointerDown}>
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={search || selectedLabel}
          onChange={handleSearchChange}
          onFocus={() => {
            if (disabled) return;
            if (!showOptions) setListScrollTop(0);
            setShowOptions(true);
          }}
          onBlur={() => {
            if (isNativeDestinationPicker && !nativePickerTransitionRef.current) {
              setNativePickerMode('idle');
            }
          }}
          onClick={() => {
            if (disabled) return;
            if (!showOptions) setListScrollTop(0);
            setShowOptions(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={isNativeDestinationPicker && nativePickerMode !== 'search'}
          className={`gm-dd__input ${value ? 'has-value' : ''}`}
          autoComplete="off"
          spellCheck="false"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={showOptions}
          aria-controls={optionsId}
          aria-haspopup="listbox"
          aria-required={required ? 'true' : undefined}
          aria-invalid={ariaInvalid ?? undefined}
          aria-describedby={ariaDescribedBy}
          required={required}
        />
        <ChevronDown size={14} className="gm-dd__arrow" />
      </div>
      <AnimatePresence>
        {showOptions && (filtered.length > 0 || showCustomOption) && (
          <motion.ul
            ref={listRef}
            id={optionsId}
            className="gm-dd__list"
            role="listbox"
            onScroll={handleListScroll}
            initial={androidBrowser ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={androidBrowser ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: androidBrowser ? 0 : 0.14 }}
          >
            {virtualized && (
              <li
                className="gm-dd__spacer"
                aria-hidden="true"
                role="presentation"
                style={{ height: listStart * OPTION_ROW_HEIGHT }}
              />
            )}
            {visibleOptions.map((opt, idx) => (
              <li
                key={String(opt[valueKey])}
                onClick={() => handleSelect(opt[valueKey])}
                onMouseEnter={() => setFocusedIndex(listStart + idx)}
                className={`gm-dd__option ${focusedIndex === idx ? 'gm-dd__option--focused' : ''} ${value === opt[valueKey] ? 'gm-dd__option--selected' : ''}`}
                role="option"
                aria-selected={value === opt[valueKey]}
                aria-posinset={listStart + idx + 1}
                aria-setsize={filtered.length + (showCustomOption ? 1 : 0)}
                data-option-index={listStart + idx}
              >
                {opt[labelKey]}
              </li>
            ))}
            {virtualized && (
              <li
                className="gm-dd__spacer"
                aria-hidden="true"
                role="presentation"
                style={{ height: Math.max(0, filtered.length - listEnd) * OPTION_ROW_HEIGHT }}
              />
            )}
            {showCustomOption && (
              <li
                key="__custom__"
                onClick={() => handleSelect(customSearch)}
                className="gm-dd__option gm-dd__option--custom"
                role="option"
                aria-selected="false"
                aria-posinset={filtered.length + 1}
                aria-setsize={filtered.length + 1}
              >
                Adicionar “{customSearch}”
              </li>
            )}
          </motion.ul>
        )}
      </AnimatePresence>
        {showOptions && remoteLoading && customSearch.length >= 2 && (
          <div className="gm-dd__empty">A procurar locais…</div>
        )}
        {showOptions && !remoteLoading && filtered.length === 0 && !showCustomOption && (
          <div className="gm-dd__empty">{emptyMessage}</div>
      )}
    </div>
  );
};

SearchableDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  required: PropTypes.bool,
  id: PropTypes.string,
  allowCustom: PropTypes.bool,
  remoteOptions: PropTypes.array,
  remoteLoading: PropTypes.bool,
  onSearch: PropTypes.func,
};

export default SearchableDropdown;
